/**
 * 맥스매치 Full 목록.
 * GET /admin/max-match/full/search — yearMonth 필수, isRequest는 검수 필요 건.
 */
import type { MaxMatchFullListItem } from '@/entities/max-match/api/max-match-full.api';
import {
  useDownloadMaxMatchFullExcel,
  useMaxMatchFullList,
} from '@/entities/max-match/hooks/useMaxMatchFull';
import { maxMatchApplicationStatusLabel } from '@/pages/max-match/maxMatchReviewStatus';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { FilterRow } from '@/shared/components/ui/row';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { formatRefYm } from '@/pages/fee-rates/feeRateUtils';
import { formatDateTime } from '@/shared/lib/dateFormat';
import { mapMaxMatchLiteExcelRows } from '@/pages/max-match/maxMatchExcelMapper';
import {
  downloadXlsx,
  MAX_MATCH_LITE_EXCEL_HEADER_MAP,
} from '@/shared/utils/xlsxExport';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import { Download, RefreshCw, Search, Sparkles } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const STATUS_ALL = 'ALL';
const STATUS_REQUEST = 'REQUEST';
const STATUS_FINALIZED = 'FINALIZED';

const STATUS_FILTER_OPTIONS = [
  { value: STATUS_ALL, label: '전체' },
  { value: STATUS_FINALIZED, label: '최종 확정 완료' },
  { value: STATUS_REQUEST, label: '미확정' },
] as const;

const getSelectableYearMonths = (): string[] => {
  const now = new Date();
  const months: string[] = [];
  for (let i = 0; i < 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(
      `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`,
    );
  }
  return months;
};

/** 목록 진행 상태. 구 값 REVIEW_REQUIRED·UPLOADING 은 라벨만 유지한다. */
export const fullProgressLabel = (row: MaxMatchFullListItem): string => {
  switch (row.status) {
    case 'REVIEW_REQUIRED':
      return '확인 필요';
    case 'UPLOADING':
      return '업로드 중';
    default:
      return maxMatchApplicationStatusLabel(row.status);
  }
};

const isCountIncomplete = (row: MaxMatchFullListItem): boolean =>
  (row.confirmedImageCount ?? 0) !== (row.totalImageCount ?? 0);

const isStatusWarning = (row: MaxMatchFullListItem): boolean =>
  row.status !== 'ADMIN_CONFIRMED' &&
  row.status !== 'MATCHED' &&
  row.status !== 'UNVERIFIABLE';

const MAX_MATCH_FULL_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  yearMonth: '',
  searchKeyword: '',
  status: STATUS_ALL,
};

export const MaxMatchFullPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const yearMonths = useMemo(() => getSelectableYearMonths(), []);

  const { values, setValues } = useListSearchParams({
    ...MAX_MATCH_FULL_SEARCH_DEFAULTS,
    yearMonth: yearMonths[0] ?? '',
  });

  const [yearMonth, setYearMonth] = useState(values.yearMonth);
  const [keyword, setKeyword] = useState(values.searchKeyword);

  React.useEffect(() => {
    setYearMonth(values.yearMonth);
    setKeyword(values.searchKeyword);
  }, [values.yearMonth, values.searchKeyword]);

  const search = React.useMemo(
    () => ({
      yearMonth: values.yearMonth,
      searchKeyword: values.searchKeyword || undefined,
      // 미확정 = 사용자 확정 후 관리자 검수 필요 (isRequest=true)
      isRequest: values.status === STATUS_REQUEST ? true : undefined,
    }),
    [values.yearMonth, values.searchKeyword, values.status],
  );

  const pageable = React.useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const { data, isLoading, isError, error, refetch, isFetching } =
    useMaxMatchFullList(search, pageable);
  const downloadExcel = useDownloadMaxMatchFullExcel();

  useEffect(() => {
    if (isError && error) handleApiError(error, toast);
  }, [isError, error, toast]);

  const rows = React.useMemo(() => {
    const content = data?.content ?? [];
    if (values.status !== STATUS_FINALIZED) return content;
    // 전용 쿼리가 없어 관리자 확정 건을 클라이언트에서 좁힌다
    return content.filter(
      (row) =>
        row.status === 'ADMIN_CONFIRMED' ||
        row.status === 'MATCHED' ||
        row.status === 'UNVERIFIABLE',
    );
  }, [data?.content, values.status]);
  const totalCount = data?.totalCount ?? 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      yearMonth,
      searchKeyword: keyword.trim(),
      page: 1,
    });
  };

  const handleDownload = async (
    row: MaxMatchFullListItem,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    if (!row.latestRunId) {
      toast.error('다운로드할 MaxMatch 실행 결과가 없습니다.');
      return;
    }
    try {
      const result = await downloadExcel.mutateAsync(row.latestRunId);
      downloadXlsx(
        mapMaxMatchLiteExcelRows(result),
        `맥스매치_풀_${row.maxMatchFullPrescriptionId}`,
        MAX_MATCH_LITE_EXCEL_HEADER_MAP,
      );
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const goDetail = (row: MaxMatchFullListItem) => {
    navigate({
      to: ROUTE_PATHS.MAX_MATCH_FULL_DETAIL,
      params: {
        maxMatchFullPrescriptionId: String(row.maxMatchFullPrescriptionId),
      },
    } as never);
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Sparkles}
        title="맥스매치 Full"
        description="맥스매치 Full 처방 확인 요청 및 결과 조회"
      />

      <FilterCard>
        <FilterRow gap={12} onSubmit={handleSearch}>
          <div className="space-y-1">
            <Label>등록년월</Label>
            <Select
              value={yearMonth}
              onValueChange={(value) => {
                setYearMonth(value);
                setValues({ yearMonth: value, page: 1 });
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearMonths.map((ym) => (
                  <SelectItem key={ym} value={ym}>
                    {formatRefYm(ym)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="full-keyword">검색어</Label>
            <Input
              id="full-keyword"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="업체명 또는 사용자 이메일"
              className="w-56"
            />
          </div>
          <div className="flex items-end gap-2">
            <Button type="submit" className="ruda-button">
              <Search className="mr-2 h-4 w-4" />
              검색
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="새로고침"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </FilterRow>
        <RadioGroup
          value={values.status || STATUS_ALL}
          onValueChange={(next) =>
            setValues({ status: next || STATUS_ALL, page: 1 })
          }
          className="flex flex-wrap gap-4"
          aria-label="진행 상태"
        >
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              htmlFor={`full-status-${opt.value}`}
              className="flex cursor-pointer items-center gap-2 text-sm"
            >
              <RadioGroupItem
                value={opt.value}
                id={`full-status-${opt.value}`}
              />
              {opt.label}
            </label>
          ))}
        </RadioGroup>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <LoadingSpinner tip="맥스매치 Full 목록을 불러오는 중..." />
        ) : rows.length === 0 ? (
          <EmptyState description="조회된 맥스매치 Full 항목이 없습니다." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>사용자 이메일</TableHead>
                  <TableHead>업체명</TableHead>
                  <TableHead>병의원</TableHead>
                  <TableHead>확정/처방 이미지 수</TableHead>
                  <TableHead>진행 상태</TableHead>
                  <TableHead>등록일시</TableHead>
                  <TableHead>max match</TableHead>
                  <TableHead>결과</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const countIncomplete = isCountIncomplete(row);
                  const statusWarning = isStatusWarning(row);
                  return (
                    <TableRow
                      key={row.maxMatchFullPrescriptionId}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => goDetail(row)}
                    >
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.companyName || '-'}</TableCell>
                      <TableCell>{row.clinicName}</TableCell>
                      <TableCell
                        className={
                          countIncomplete ? 'text-destructive' : undefined
                        }
                      >
                        {row.confirmedImageCount ?? 0} /{' '}
                        {row.totalImageCount ?? 0}
                      </TableCell>
                      <TableCell
                        className={
                          statusWarning ? 'text-destructive' : undefined
                        }
                      >
                        {fullProgressLabel(row)}
                      </TableCell>
                      <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                      <TableCell>
                        {formatDateTime(row.latestRunCreatedAt)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          className="ruda-button"
                          disabled={!row.latestRunId || downloadExcel.isPending}
                          onClick={(e) => handleDownload(row, e)}
                        >
                          <Download className="mr-1 h-3 w-3" />
                          다운
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>
    </div>
  );
};
